import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LuxuryInput from '../ui/LuxuryInput';
import { Send, Check, AlertCircle, User, Mail, Phone, MessageSquare } from 'lucide-react';

const ContactFormBit = ({ isMobile = false }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    const subjects = [
        'General Inquiry',
        'Product Questions',
        'Order Support',
        'Shipping & Delivery',
        'Returns & Exchanges',
        'Wholesale/Business',
        'Press & Media',
        'Careers'
    ];

    const validateForm = () => {
        if (!formData.name.trim()) return 'Please enter your name';
        if (!formData.email.trim()) return 'Please enter your email';
        if (!/\S+@\S+\.\S+/.test(formData.email)) return 'Please enter a valid email';
        if (!formData.subject) return 'Please select a subject';
        if (!formData.message.trim()) return 'Please enter your message';
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        setIsLoading(true);

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        setIsLoading(false);
        setIsSubmitted(true);

        // Reset form after 3 seconds
        setTimeout(() => {
            setIsSubmitted(false);
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
        }, 3000);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (error) setError('');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="luxury-card p-4 md:p-8"
        >
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-champagne" />
                </div>
                <div>
                    <h2 className="font-heading text-xl md:text-2xl font-medium text-charcoal">
                        Send us a message
                    </h2>
                    <p className="text-sm text-charcoal/50">
                        We'll respond within 30 minutes
                    </p>
                </div>
            </div>

            <AnimatePresence mode="wait">
                {isSubmitted ? (
                    <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="text-center py-12"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 15 }}
                            className="w-16 h-16 rounded-full bg-gradient-to-br from-sage to-sage/80 flex items-center justify-center mx-auto mb-4"
                        >
                            <Check className="w-8 h-8 text-white" />
                        </motion.div>
                        <h3 className="text-xl font-medium text-charcoal mb-2">
                            Message Sent
                        </h3>
                        <p className="text-charcoal/60">
                            Thank you for reaching out. Our beauty advisors will<br />
                            respond to your inquiry within 30 minutes.
                        </p>
                    </motion.div>
                ) : (
                    <motion.form
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleSubmit}
                        className="space-y-4 md:space-y-6"
                    >
                        {/* Name & Email */}
                        <div className="grid md:grid-cols-2 gap-4">
                            <LuxuryInput
                                label="Full Name"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                icon={User}
                                size={isMobile ? "medium" : "large"}
                                required
                            />

                            <LuxuryInput
                                type="email"
                                label="Email Address"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                icon={Mail}
                                size={isMobile ? "medium" : "large"}
                                required
                            />
                        </div>

                        {/* Phone & Subject */}
                        <div className="grid md:grid-cols-2 gap-4">
                            <LuxuryInput
                                type="tel"
                                label="Phone Number"
                                value={formData.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                icon={Phone}
                                size={isMobile ? "medium" : "large"}
                            />

                            <div className="relative">
                                <label className="block text-sm font-medium text-charcoal/70 mb-2">
                                    Subject <span className="text-champagne">*</span>
                                </label>
                                <select
                                    value={formData.subject}
                                    onChange={(e) => handleChange('subject', e.target.value)}
                                    className="w-full px-4 py-3 bg-white border border-nude rounded-xl text-charcoal focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/30 appearance-none cursor-pointer"
                                    required
                                >
                                    <option value="">Select a topic</option>
                                    {subjects.map((subject) => (
                                        <option key={subject} value={subject}>{subject}</option>
                                    ))}
                                </select>
                                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                    <svg className="w-4 h-4 text-charcoal/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Message */}
                        <div>
                            <label className="block text-sm font-medium text-charcoal/70 mb-2">
                                Your Message <span className="text-champagne">*</span>
                            </label>
                            <textarea
                                value={formData.message}
                                onChange={(e) => handleChange('message', e.target.value)}
                                rows={isMobile ? 4 : 6}
                                className="w-full px-4 py-3 bg-white border border-nude rounded-xl text-charcoal focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/30 resize-none transition-all duration-300"
                                placeholder="Tell us about your inquiry, product questions, or how we can support your KROMAIC ritual..."
                                required
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-xl"
                            >
                                <AlertCircle className="w-4 h-4 text-error" />
                                <span className="text-sm text-error">{error}</span>
                            </motion.div>
                        )}

                        {/* Submit Button */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`
                  w-full px-6 md:px-8 py-3 md:py-4 rounded-full
                  bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal/90
                  text-pearl font-medium text-base md:text-lg
                  hover:from-charcoal/90 hover:via-charcoal/80 hover:to-charcoal/70
                  active:scale-95
                  transition-all duration-300
                  shadow-lg hover:shadow-xl
                  disabled:opacity-50 disabled:cursor-not-allowed
                  relative overflow-hidden group
                `}
                            >
                <span className="relative flex items-center justify-center gap-3">
                  {isLoading ? (
                      <>
                          <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                          Sending...
                      </>
                  ) : (
                      <>
                          <Send className="w-5 h-5"/>
                          Send Message
                      </>
                  )}
                </span>

                                {/* Shimmer effect */}
                                <span
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>
                            </button>
                        </motion.div>

                        {/* Privacy Note */}
                        <p className="text-xs text-center text-charcoal/40 pt-4">
                            By submitting this form, you agree to our Privacy Policy.<br />
                            Your data is encrypted and never shared with third parties.
                        </p>
                    </motion.form>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default ContactFormBit;